import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';

type TextPart = Extract<ChatPart, { kind: 'text' }>;
type ToolCallPart = Extract<ChatPart, { kind: 'tool-call' }>;

const SUB_AGENT_TOOL_NAME_PREFIX = 'agent-';

/** One row in the thinking-section timeline for an assistant message. */
export interface TimelineToolEvent {
  kind: 'tool';
  toolCallId: string;
  name: string;
  status: ToolCallPart['status'];
  /** Provenance — non-empty when this row came from a flattened `agent-*` sub-agent. */
  subAgentToolName?: string;
}

/** Reasoning row in the thinking-section timeline (rendered via the text-part component). */
export interface TimelineReasoningEvent {
  kind: 'reasoning';
  messageId: string;
  content: string;
  done: boolean;
  /** Provenance — non-empty when this row came from a flattened `agent-*` sub-agent. */
  subAgentToolName?: string;
}

/** Discriminated union of all thinking-section row kinds. */
export type TimelineEvent = TimelineToolEvent | TimelineReasoningEvent;

/**
 * One entry in the assistant message body (the part rendered below the thinking section).
 * `render-tool` entries mount their registered presenter component; `text` entries render markdown.
 */
export type BodyEntry =
  | { kind: 'text'; part: TextPart }
  | { kind: 'render-tool'; part: ToolCallPart };

/** Split of an assistant `ChatMessage` into timeline rows + body entries. */
export interface AssistantMessageView {
  timeline: TimelineEvent[];
  body: BodyEntry[];
}

/**
 * Split an assistant message into a thinking timeline + a renderable body.
 *
 * `renderToolNames` is the set of tool ids that have a registered FE presenter — those
 * tool-call parts become `render-tool` body entries; every other tool-call becomes a
 * timeline row. Sub-agent (`agent-*`) tool calls are flattened: their inner reasoning
 * and tool steps inline into the parent timeline, tagged with `subAgentToolName`.
 */
export const splitAssistantMessage = (
  msg: ChatMessage,
  renderToolNames: ReadonlySet<string>,
): AssistantMessageView => {
  if (msg.role !== 'assistant') return { timeline: [], body: [] };

  const body = msg.parts.flatMap((part): BodyEntry[] => {
    if (part.kind === 'text') return [{ kind: 'text', part }];
    if (part.kind === 'tool-call' && renderToolNames.has(part.name)) {
      return [{ kind: 'render-tool', part }];
    }
    return [];
  });

  const timeline = collectTimeline(msg.parts, renderToolNames, { flattenText: false });

  return { timeline, body };
};

interface CollectContext {
  /** Provenance tag — set when walking inside a flattened `agent-*` sub-agent message. */
  subAgentToolName?: string;
  /** When true, text parts are converted to reasoning events (sub-agent only). */
  flattenText: boolean;
}

function collectTimeline(
  parts: readonly ChatPart[],
  renderToolNames: ReadonlySet<string>,
  context: CollectContext,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  for (const part of parts) {
    if (part.kind === 'reasoning') {
      events.push({
        kind: 'reasoning',
        messageId: part.messageId,
        content: part.content,
        done: part.done,
        ...(context.subAgentToolName !== undefined
          ? { subAgentToolName: context.subAgentToolName }
          : {}),
      });
      continue;
    }
    if (part.kind === 'text' && context.flattenText) {
      events.push({
        kind: 'reasoning',
        messageId: part.messageId,
        content: part.content,
        done: part.done,
        ...(context.subAgentToolName !== undefined
          ? { subAgentToolName: context.subAgentToolName }
          : {}),
      });
      continue;
    }
    if (part.kind !== 'tool-call') continue;
    if (renderToolNames.has(part.name)) continue;

    if (part.name.startsWith(SUB_AGENT_TOOL_NAME_PREFIX)) {
      for (const subMsg of part.subAgentMessages ?? []) {
        events.push(
          ...collectTimeline(subMsg.parts, renderToolNames, {
            subAgentToolName: part.name,
            flattenText: true,
          }),
        );
      }
      continue;
    }

    events.push({
      kind: 'tool',
      toolCallId: part.toolCallId,
      name: part.name,
      status: part.status,
      ...(context.subAgentToolName !== undefined
        ? { subAgentToolName: context.subAgentToolName }
        : {}),
    });
  }
  return events;
}
